/* screens/analytics/ResponsiveTable.jsx — единая обёртка таблиц Аналитики.

   Что чинит. Инструментальный прогон показал, что на телефоне таблицы отчётов
   существуют только как горизонтальная лента: ABC требовала 1347px прокрутки вбок
   при ширине экрана 380px, «Официанты» — 1082px, «Ликвидность» — 705px. Формально
   это «overflow-x: auto», то есть не сломано; практически — тринадцать колонок,
   которые невозможно сопоставить, потому что имя блюда уезжает из виду на первом же
   движении пальца.

   Два режима одной разметки:

   • ШИРОКО (контейнер ≥ 720px) — обычная таблица с залипающей ПЕРВОЙ колонкой:
     имя позиции остаётся на экране, пока листаешь числа вбок. Раньше именно её
     потеря делала боковую прокрутку бессмысленной. Шапка при этом НЕ залипает и
     залипнуть не может: обёртка прокручивается по горизонтали, а значит она же
     становится скроллпортом для sticky — до страницы шапке не дотянуться.
     Единственный обход — вертикальный скролл внутри таблицы, но на вложенных
     скролл-областях продукт уже обжигался, и ради удобства второго порядка их
     не возвращаем.

   • УЗКО (< 720px) — каждая строка становится карточкой «подпись → значение».
     Подписи берутся из шапки таблицы и проставляются в data-label; вся раскладка
     дальше — CSS. Так режим получают ВСЕ таблицы разом, без переписывания семи
     отчётов на новый табличный компонент (сортировка, фильтры-воронки, клики по
     строкам и drawer'ы остаются ровно теми же).

   Порог — container query, а не @media: таблицы живут и внутри drawer'ов, где
   ширина окна ничего не говорит о ширине колонки (тот же приём, что в редакторах).

   Зеркалирование подписей — единственная запись в DOM, и только в data-атрибуты,
   которыми React не управляет: конфликта с реконсиляцией нет. Делается ТОЛЬКО в
   карточном режиме — на широком экране результат не используется, а таблица на
   900 позиций это 13 500 ячеек на каждую сортировку.

   Ширина колонок вручную — проп `widths` (настройки из useTableSettings): обёртка
   отдаёт их ручкам в шапке и colgroup-у таблицы (TableCols) вместе с флагом
   карточного режима (ColumnWidths.jsx). Без пропа таблица ровно прежняя. */
import React from 'react';
import { TableWidthsProvider } from './ColumnWidths.jsx';

const { useRef, useEffect, useState } = React;

/** Текст заголовка колонки без служебных глифов сортировки/фильтра. */
function headLabel(th) {
  const own = th.querySelector('.rt-th__label, .an-th__label');
  const src = own || th;
  return (src.textContent || '').replace(/\s+/g, ' ').trim();
}

/* Подписи колонок по МНОГОРЯДНОЙ шапке.
   Брать последнюю строку thead нельзя: в ABC шапка из двух рядов — сквозные
   колонки («#», «Блюдо») объявлены с rowSpan=2 в ПЕРВОМ ряду, а во втором стоят
   только метрики внутри групп. Наивный разбор сдвинул бы все подписи и подписал
   выручку «количеством» — то есть соврал бы, а не просто съехал.

   Считаем занятость клеток, как это делает сам браузер: каждая ячейка занимает
   colSpan колонок на rowSpan рядов вперёд. Подпись колонки — у той ячейки,
   которая накрывает её в ПОСЛЕДНЕМ ряду шапки (для сквозных это она же). */
function columnLabels(table) {
  const rows = Array.from(table.querySelectorAll('thead tr'));
  if (!rows.length) return [];
  const grid = [];                       // grid[row][col] = th
  rows.forEach((tr, r) => {
    grid[r] = grid[r] || [];
    let c = 0;
    for (const th of tr.children) {
      while (grid[r][c] !== undefined) c += 1;      // клетка занята сверху
      const cs = parseInt(th.getAttribute('colspan') || '1', 10) || 1;
      const rs = parseInt(th.getAttribute('rowspan') || '1', 10) || 1;
      for (let dr = 0; dr < rs; dr += 1) {
        grid[r + dr] = grid[r + dr] || [];
        for (let dc = 0; dc < cs; dc += 1) grid[r + dr][c + dc] = th;
      }
      c += cs;
    }
  });
  const last = grid[rows.length - 1] || [];
  return last.map((th) => (th ? headLabel(th) : ''));
}

// Тот же порог, что у container query карточного режима в analytics-redesign.css.
const CARD_WIDTH = 720;

export function ResponsiveTable({ children, className = '', stickyFirst = true, cards = true,
                                  widths = null }) {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [more, setMore] = useState(false);

  /* Ширину контейнера читаем сами, хотя раскладку решает CSS: разметку — подписи
     ячеек и роли — нужно готовить ТОЛЬКО для карточного режима, а в нём таблица
     на 900 позиций это 13 500 ячеек. Без этой развилки обход шёл на каждый рендер
     и на широком экране тоже, то есть каждая сортировка колонки платила за работу,
     результат которой там не используется.
     Узкий контейнер считаем и у таблиц БЕЗ карточек (`cards={false}`, ОПиУ): он
     нужен ширинам колонок — уже 720px ручек и colgroup нет у всех таблиц. */
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    /* `<=`, а не `<`. Порог считается в двух местах по-разному: здесь по
       clientWidth, который ОКРУГЛЁН до целого, а в CSS — по дробной ширине
       content-box (`@container antable (max-width: 719.98px)`). На окне 804px
       content-box равен 719.781: container query уже разворачивает строки в
       карточки, а clientWidth округляется в 720, и строгое `<` оставляло narrow
       ложным — карточки рисовались с ПУСТЫМИ подписями, потому что data-label
       никто не проставил. Один лишний пиксель в пользу карточного режима стоит
       дешевле, чем карточка без подписей. */
    const sync = () => setNarrow(root.clientWidth > 0 && root.clientWidth <= CARD_WIDTH);
    sync();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    return () => ro.disconnect();
  }, [cards]);

  /* Есть ли содержимое ЗА правым краем.

     Прокрутка вбок у широкой таблицы намеренная, но себя не показывала:
     overlay-скроллбар не виден, пока его не тронешь, и колонка, которая не
     влезла, читается как обрезанная. Так и вышло с FC% в «Блюдах разреза» —
     срезано было 21px, а выглядело как поломка. Флаг включает затенение правого
     края (CSS `.has-end`).

     Смотрим и на сам скроллпорт, и на таблицу: содержимое меняется от сортировки
     и подгрузки строк, а размеры скроллпорта при этом те же. На раскладку флаг
     не влияет — затенение рисуется абсолютным ::after, — поэтому обратной связи
     «класс меняет ширину, ширина меняет класс» здесь нет. */
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return undefined;
    const sync = () => setMore(sc.scrollWidth - sc.clientWidth - sc.scrollLeft > 1);
    sync();
    sc.addEventListener('scroll', sync, { passive: true });
    if (typeof ResizeObserver === 'undefined') return () => sc.removeEventListener('scroll', sync);
    const ro = new ResizeObserver(sync);
    ro.observe(sc);
    const table = sc.querySelector('table');
    if (table) ro.observe(table);
    return () => { sc.removeEventListener('scroll', sync); ro.disconnect(); };
  }, [narrow, children]);

  useEffect(() => {
    if (!cards || !narrow) return;
    const root = ref.current;
    if (!root) return;
    const table = root.querySelector('table');
    if (!table) return;

    /* Явные роли таблицы. В карточном режиме строкам ставится display:block, а это
       СНИМАЕТ у браузера неявные табличные роли — таблица превращается для
       скринридера в набор безымянных блоков. Проставляем роли руками: они
       удерживают структуру «таблица → строка → ячейка» там, где CSS её сломал. */
    /* Роль, написанную РУКАМИ, не перебиваем. Строка-ссылка объявлена
       role="button" (клик открывает карточку официанта, блюда разреза, деталку
       поставщика) — и первая версия этого кода молча меняла её на "row" в
       карточном режиме. Строка оставалась кликабельной мышью, но для
       скринридера превращалась в обычную строку с tabindex, то есть в фокус,
       который непонятно зачем берут. Проверку это тоже сбило: селектор
       `tr[role="button"]` на мобильных ширинах не находил ни одной строки, и
       прогон рапортовал «нет кликабельных строк» вместо дефекта. */
    const keep = (el, role) => { if (!el.hasAttribute('role')) el.setAttribute('role', role); };
    keep(table, 'table');
    for (const g of table.querySelectorAll('thead, tbody, tfoot')) keep(g, 'rowgroup');
    for (const tr of table.querySelectorAll('tr')) keep(tr, 'row');
    for (const th of table.querySelectorAll('thead th')) keep(th, 'columnheader');
    for (const td of table.querySelectorAll('tbody td, tfoot td')) keep(td, 'cell');

    const labels = columnLabels(table);
    if (!labels.length) return;
    for (const row of table.querySelectorAll('tbody tr')) {
      let col = 0;
      for (const cell of row.children) {
        const span = parseInt(cell.getAttribute('colspan') || '1', 10) || 1;
        // Подпись, написанную РУКАМИ в разметке, не трогаем. В ABC у ячеек уже
        // стоят свои data-label, и они точнее выведенных из шапки: там сдвоенный
        // заголовок «Количество / шт.», а в разметке — «Количество, шт.» и
        // «ABC количество» вместо двух одинаковых «ABC». Свои подписи помечаем
        // data-label-auto, чтобы на следующем проходе отличать их от авторских.
        const mine = cell.hasAttribute('data-label-auto');
        const has = cell.hasAttribute('data-label');
        if (has && !mine) { col += span; continue; }
        // Ячейка на несколько колонок (итоги, разделители групп) подписи не получает —
        // в карточном режиме она занимает всю ширину, как и в таблице.
        if (span === 1 && labels[col]) {
          cell.setAttribute('data-label', labels[col]);
          cell.setAttribute('data-label-auto', '');
        } else if (mine) {
          cell.removeAttribute('data-label');
          cell.removeAttribute('data-label-auto');
        }
        col += span;
      }
    }
  });

  /* `is-cards` — карточный режим ВКЛЮЧЁН прямо сейчас (в отличие от `--cards`,
     который лишь разрешает его). Нужен потому, что рамку и обрезку самой обёртки
     нельзя снять из @container: контейнер объявлен на ней же, а элемент по
     спецификации не может быть собственным query-контейнером. */
  const cls = 'an-tablewrap'
    + (stickyFirst ? ' an-tablewrap--sticky1' : '')
    + (cards ? ' an-tablewrap--cards' : '')
    + (cards && narrow ? ' is-cards' : '')
    + (more ? ' has-end' : '')
    + (className ? ' ' + className : '');

  return (
    <div className={cls} ref={ref}>
      <div className="an-tablescroll" tabIndex={0} role="group" ref={scrollRef}>
        <TableWidthsProvider settings={widths} narrow={narrow}>
          {children}
        </TableWidthsProvider>
      </div>
    </div>
  );
}
